import { create } from 'zustand';
import type {
  StrategyDefinition,
  ComponentMeta,
  GoalPreset,
} from '../api/strategyApi';
import {
  fetchStrategies,
  fetchComponents,
  fetchPresets,
} from '../api/strategyApi';

// ── Types ──────────────────────────────────────────────────────

export type BuilderMode = 'list' | 'wizard' | 'expert';

type StrategyBuilderState = {
  mode: BuilderMode;
  wizardStep: number;
  strategies: StrategyDefinition[];
  components: ComponentMeta[];
  presets: Record<string, GoalPreset>;
  draft: Partial<StrategyDefinition>;
  loading: boolean;
  error: string | null;

  setMode: (mode: BuilderMode) => void;
  setWizardStep: (step: number) => void;
  setDraft: (patch: Partial<StrategyDefinition>) => void;
  applyPreset: (goal: string) => void;
  addComponent: (id: string) => void;
  removeComponent: (index: number) => void;
  setComponentWeight: (index: number, weight: number) => void;
  setTfWeight: (tf: string, weight: number) => void;
  loadStrategies: () => Promise<void>;
  loadComponents: () => Promise<void>;
  loadPresets: () => Promise<void>;
  reset: () => void;
  editStrategy: (strategy: StrategyDefinition) => void;
};

// ── Defaults ───────────────────────────────────────────────────

const DEFAULT_TF_WEIGHTS: Record<string, number> = {
  '1': 0.03,
  '5': 0.07,
  '15': 0.15,
  '60': 0.25,
  '240': 0.25,
  '1440': 0.25,
};

const EMPTY_DRAFT: Partial<StrategyDefinition> = {
  name: '',
  description: '',
  goal: 'custom',
  components: [],
  tfWeights: { ...DEFAULT_TF_WEIGHTS },
  buyThreshold: 0.5,
  sellThreshold: -0.5,
  riskPerTradePct: 1,
  slAtrMultiplier: 1.5,
  tpAtrMultiplier: 2.5,
  trailingActivationAtr: 1.5,
  trailingStepAtr: 0.3,
  maxDrawdownPct: 15,
  cooldownCandles: 3,
  enabled: true,
  assignedSymbols: [],
};

// ── Store ──────────────────────────────────────────────────────

export const useStrategyBuilderStore = create<StrategyBuilderState>()(
  (set, get) => ({
    mode: 'list',
    wizardStep: 0,
    strategies: [],
    components: [],
    presets: {},
    draft: { ...EMPTY_DRAFT },
    loading: false,
    error: null,

    setMode: (mode) => set({ mode }),
    setWizardStep: (step) => set({ wizardStep: step }),

    setDraft: (patch) =>
      set((state) => ({ draft: { ...state.draft, ...patch } })),

    applyPreset: (goal) => {
      const preset = get().presets[goal];
      if (!preset) return;
      set({
        draft: {
          name: preset.name,
          description: preset.description,
          goal: goal as StrategyDefinition['goal'],
          components: [...preset.components],
          tfWeights: { ...preset.tfWeights },
          buyThreshold: preset.buyThreshold,
          sellThreshold: preset.sellThreshold,
          riskPerTradePct: preset.riskPerTradePct,
          slAtrMultiplier: preset.slAtrMultiplier,
          tpAtrMultiplier: preset.tpAtrMultiplier,
          trailingActivationAtr: preset.trailingActivationAtr,
          trailingStepAtr: preset.trailingStepAtr,
          maxDrawdownPct: preset.maxDrawdownPct,
          cooldownCandles: preset.cooldownCandles,
          enabled: true,
          assignedSymbols: [],
        },
        wizardStep: 1,
      });
    },

    addComponent: (id) =>
      set((state) => {
        const existing = state.draft.components ?? [];
        if (existing.some((c) => c.id === id)) return state;
        return {
          draft: {
            ...state.draft,
            components: [...existing, { id, weight: 50, params: {} }],
          },
        };
      }),

    removeComponent: (index) =>
      set((state) => {
        const existing = [...(state.draft.components ?? [])];
        existing.splice(index, 1);
        return { draft: { ...state.draft, components: existing } };
      }),

    setComponentWeight: (index, weight) =>
      set((state) => {
        const existing = [...(state.draft.components ?? [])];
        const item = existing[index];
        if (item) existing[index] = { ...item, weight };
        return { draft: { ...state.draft, components: existing } };
      }),

    setTfWeight: (tf, weight) =>
      set((state) => ({
        draft: {
          ...state.draft,
          tfWeights: { ...state.draft.tfWeights, [tf]: weight },
        },
      })),

    loadStrategies: async () => {
      set({ loading: true, error: null });
      try {
        const strategies = await fetchStrategies();
        set({ strategies, loading: false });
      } catch (e) {
        set({ error: (e as Error).message, loading: false });
      }
    },

    loadComponents: async () => {
      try {
        const components = await fetchComponents();
        set({ components });
      } catch (e) {
        set({ error: (e as Error).message });
      }
    },

    loadPresets: async () => {
      try {
        const presets = await fetchPresets();
        set({ presets });
      } catch (e) {
        set({ error: (e as Error).message });
      }
    },

    reset: () =>
      set({
        mode: 'list',
        wizardStep: 0,
        draft: { ...EMPTY_DRAFT },
        error: null,
      }),

    editStrategy: (strategy) =>
      set({
        mode: 'expert',
        draft: { ...strategy },
        error: null,
      }),
  }),
);
